/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.0.0
 */

import { createElement } from 'react';

import uniqBy from 'lodash/uniqBy';
import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';

import { checkResponseErrorWithModal, requestSaga } from '@/main/sagas/requestSaga'
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import { API_HOSTNAME, INQUIRY_FILE, INQUIRY_MODAL, INQUIRY_TOPIC, INQUIRY_USER } from '@modules/app/Api';
import { logBusinessEvent } from '@modules/logging/loggingActions';
import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging/loggingConstants';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';

import InquiryCreatedModal, { IInquiryCreatedModalProps } from './components/InquiryCreatedModal';
import {
    modalInquiryAttachFileFailed,
    modalInquiryAttachFileSuccessful,
    modalInquiryCreateFailed,
    modalInquiryDetachFileFailed,
    modalInquiryDetachFileSuccessful,
    modalInquiryLoadTopicFailed,
    modalInquiryLoadTopicListFailed,
    modalInquiryLoadTopicListSuccessful,
    modalInquiryLoadTopicSuccessful,
    modalInquiryLoadUserFailed,
    modalInquiryLoadUserSuccessful,
} from './createInquiryActions';
import {
    MODAL_INQUIRY_ATTACH_FILE,
    MODAL_INQUIRY_CREATE,
    MODAL_INQUIRY_DETACH_FILE,
    MODAL_INQUIRY_LOAD_TOPIC,
    MODAL_INQUIRY_LOAD_TOPIC_LIST,
    MODAL_INQUIRY_LOAD_USER,
} from './createInquiryConstants';
import { initialCreateInquiryState } from './createInquiryReducer';
import { makeSelectorInquiryAttachList } from './createInquirySelectors';
import {
    IModalInquiryAttachFileAction,
    IModalInquiryCreateAction,
    TModalInquiryDetachFileAction,
    TModalInquiryLoadTopicAction,
    TModalInquiryLoadTopicListAction,
    TModalInquiryLoadUserAction,
} from './types/createInquiryActionTypes';
import { IInquiryAttachProps, ITopicOptions } from './types/createInquiryTypes';

export function* modalInquiryLoadUserSaga(action: TModalInquiryLoadUserAction): SagaIterator {

    const payload = action.payload;

    try {
        const URL = `${API_HOSTNAME}${INQUIRY_MODAL}${INQUIRY_USER}/${payload.type}/${payload.userId}`;
        const response = yield call(requestSaga, URL, 'GET');
        const userInfo = response ? response : initialCreateInquiryState.userInfo;
        yield put(modalInquiryLoadUserSuccessful(userInfo));
        yield fork(modalInquiryLoadTopicListSaga, {
            payload: {
                type: payload.type,
                subscriberId: userInfo.subsId,
            },
            type: MODAL_INQUIRY_LOAD_TOPIC_LIST,
        });
    } catch (e: any) {
        yield put(modalInquiryLoadUserFailed(e));

        yield call(checkResponseErrorWithModal, e);
    }
}

export function* modalInquiryLoadTopicListSaga(action: TModalInquiryLoadTopicListAction): SagaIterator {

    const payload = action.payload;

    try {
        const subsId = payload.subscriberId ? payload.subscriberId : '';
        const URL = `${API_HOSTNAME}${INQUIRY_MODAL}${INQUIRY_TOPIC}?type=${payload.type}&subsId=${subsId}`;
        const response = yield call(requestSaga, URL, 'GET');
        const topicList = uniqBy<ITopicOptions>(response, 'id');
        const options = topicList.map((topic: ITopicOptions) => {
            return {
                label: topic.def,
                value: topic.id,
            };
        });
        yield put(modalInquiryLoadTopicListSuccessful(options));
    } catch (e: any) {
        yield put(modalInquiryLoadTopicListFailed(e));

        yield call(checkResponseErrorWithModal, e);
    }
}

export function* modalInquiryLoadTopicSaga(action: TModalInquiryLoadTopicAction): SagaIterator {

    const payload = action.payload;

    try {
        yield put(logBusinessEvent({
            eventName: ELogBusinessEventName.USER_CHOOSE_INQUIRY_TOPIC,
            type: ELogBusinessEventType.common,
        }));
        const subsId = payload.subscriberId ? 'subscriberId=' + payload.subscriberId : '';
        const clntId = payload.clientId ? 'clntId=' + payload.clientId : '';
        const queryParam = subsId ? subsId : clntId;
        const URL = `${API_HOSTNAME}${INQUIRY_MODAL}${INQUIRY_TOPIC}/${payload.topicId}?${queryParam}`;
        const response = yield call(requestSaga, URL, 'GET');
        if (response) {
            yield put(modalInquiryLoadTopicSuccessful(response));
        }
        yield put(logBusinessEvent({
            eventName: ELogBusinessEventName.B2B_SHOW_INQUIRY_PARAMETERS,
            type: ELogBusinessEventType.common,
        }));
    } catch (e: any) {
        yield put(modalInquiryLoadTopicFailed(e));

        yield call(checkResponseErrorWithModal, e);
    }
}

export function* modalInquiryCreateSaga(action: IModalInquiryCreateAction): SagaIterator {

    const payload = action.payload.params;

    try {
        yield put(logBusinessEvent({
            eventName: ELogBusinessEventName.USER_CREATE_INQUIRY,
            type: ELogBusinessEventType.common,
        }));
        const URL = `${API_HOSTNAME}${INQUIRY_MODAL}`;
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'X-NODE': 'sticky',
            },
        };
        const response = yield call(requestSaga, URL, 'POST', payload, options);
        const id = response?.id ?? '';
        const fileAttachedWithErrors = response?.fileAttachedWithErrors ?? false;
        action.payload.callback();
        if (id) {
            yield put(modalOpen({
                render: (props: IModalRenderProps) =>
                    createElement<IInquiryCreatedModalProps>(InquiryCreatedModal, {
                        fileAttachedWithErrors,
                        inquiryNumber: id,
                        onCloseModal: props.onCloseModal,
                        showInqLink: !!id,
                    }),
            }));
        }
        yield put(logBusinessEvent({
            eventName: ELogBusinessEventName.B2B_CREATE_INQUIRY,
            type: ELogBusinessEventType.common,
        }));
        GTMOperations.navigation.inquirySuccessfulCreateOperation();
    } catch (e: any) {
        GTMOperations.navigation.inquiryErrorCreateOperation(e);
        yield put(modalInquiryCreateFailed(e));

        yield call(checkResponseErrorWithModal, e);
        yield put(modalInquiryDetachFileSuccessful([]));
    }
}

export function* modalInquiryAttachFileSaga(action: IModalInquiryAttachFileAction): SagaIterator {

    const payload = action.payload;

    try {
        const data = new FormData();
        data.append('file', payload.file);
        const URL = `${API_HOSTNAME}${INQUIRY_MODAL}${INQUIRY_FILE}/${payload.type}`;

        const options = {
            headers: {
                'accept': 'application/json;charset=UTF-8',
                'X-NODE': 'sticky',
            },
        };
        const response = yield call(requestSaga, URL, 'POST', data, options);
        const fileId = response?.fileId ?? -1;
        yield put(modalInquiryAttachFileSuccessful({ fileId, fileName: payload.file.name }));
    } catch (e: any) {
        yield put(modalInquiryAttachFileFailed(e));
    }
}

export function* modalInquiryDetachFileAction(action: TModalInquiryDetachFileAction): SagaIterator {

    const payload = action.payload;

    try {
        const attachList = yield select(makeSelectorInquiryAttachList());
        const URL = `${API_HOSTNAME}${INQUIRY_MODAL}${INQUIRY_FILE}/${payload}`;
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'X-NODE': 'sticky',
            },
        };
        yield call(requestSaga, URL, 'DELETE', null, options);
        const newAttachList = (attachList || []).filter((attach: IInquiryAttachProps) => attach.fileId !== payload);
        yield put(modalInquiryDetachFileSuccessful(newAttachList));
    } catch (e: any) {
        yield put(modalInquiryDetachFileFailed(e));
    }
}

export function* createInquirySaga(): SagaIterator {
    yield all([
        takeEvery(MODAL_INQUIRY_ATTACH_FILE, modalInquiryAttachFileSaga),
        takeEvery(MODAL_INQUIRY_CREATE, modalInquiryCreateSaga),
        takeEvery(MODAL_INQUIRY_DETACH_FILE, modalInquiryDetachFileAction),
        takeEvery(MODAL_INQUIRY_LOAD_TOPIC, modalInquiryLoadTopicSaga),
        takeEvery(MODAL_INQUIRY_LOAD_TOPIC_LIST, modalInquiryLoadTopicListSaga),
        takeEvery(MODAL_INQUIRY_LOAD_USER, modalInquiryLoadUserSaga),
    ]);
}
